/**
 * LegBetCardView — presentational render for LegBetCard.
 * Extracted verbatim from components/LegBetCard.jsx (god-file decomposition).
 * Pure view: receives all derived values/handlers/props as props. Completeness
 * machine-verified by scripts/check-extraction.js; the call site is generated
 * from the SAME prop list, so it cannot drift out of sync.
 */

import { View, Text, TextInput, TouchableOpacity, Pressable, ScrollView, Image, Platform } from 'react-native';
import GradientButton from './GradientButton';
import { getSportsbookLogo, getSportsbookUrl, isPlayerProp } from '../data/logos';
import { styles } from './legBetCardStyles';


export default function LegBetCardView({ awayLogoUrl, bestEvData, bestPrice, bookCount, canShowMath, defaultSelectedBook, detectSportType, displayEvMethods, ev, extractPrice, handleCardPress, handleMouseEnter, handleMouseLeave, handleSelectLeg, handleToggleMath, highlightPreferredBook, homeLogoUrl, implied, isCompactLayout, isEnded, isLive, isMobileLayout, isNoOddsYet, isSelected, isWatching, kellyUnits, logoUrl, marketAverage, marketAverageImplied, onOpenDeepLink, onUnwatchFixture, onWatchFixture, option, payout, price, selectedBook, setSelectedBook, setSortBooksBy, setStake, showMath, sortBooksBy, sportsbookLogoUrl, stakeNum, suggestedWager, teamLogoUrl, userSelectedSportsbooks, workingOption }) {
    return (
        <Pressable 
            style={[
                styles.betCard, 
                isSelected && styles.selectedBetCard,
                // Yellow border for upcoming games
                isNoOddsYet && !isLive && !isEnded && { opacity: 0.7, borderColor: 'rgba(255, 193, 7, 0.4)', borderWidth: 1 },
                // Green border for live games
                isLive && { borderColor: 'rgba(76, 175, 80, 0.6)', borderWidth: 2 },
                // Grey border for ended games
                isEnded && { opacity: 0.6, borderColor: 'rgba(158, 158, 158, 0.4)', borderWidth: 1 },
                // Thicker border when selected
                isSelected && !isLive && !isEnded && { 
                    borderWidth: 3,
                    borderColor: '#A259FF',
                    shadowColor: '#A259FF',
                    shadowOpacity: 0.3,
                    shadowRadius: 12,
                    elevation: 8
                }
            ]} 
            testID="bet-card-pressable"
            onPress={isNoOddsYet ? undefined : handleCardPress} // Disable press for "no odds yet" cards
            {...(Platform.OS === 'web' && !isNoOddsYet ? { 
                onMouseEnter: handleMouseEnter, 
                onMouseLeave: handleMouseLeave
            } : {})}
        >
            {/* ⭐ TWO-COLUMN LAYOUT: Left (info+math+buttons), Right (sportsbooks full height) */}
            {/* ⭐ RESPONSIVE: Stack columns on mobile/tablet viewports */}
            <View style={[styles.betCardContent, { opacity: 1, paddingLeft: isCompactLayout ? 12 : 24, paddingRight: isCompactLayout ? 12 : 18 }]}>
                <View style={[
                    styles.twoColumnLayout, 
                    isMobileLayout && { flexDirection: 'column', gap: 16 }
                ]}>
                    {/* LEFT COLUMN: Bet Info + Math + Action Buttons */}
                    <View style={[styles.leftColumn, isMobileLayout && { minWidth: '100%' }]}>
                        {/* ROW 1: Title, Description, Date/Time, Logos */}
                        <View style={styles.infoSection}>
                            {/* ⭐ REDESIGNED HEADER: Player Photo + Title + Badges */}
                            <View style={[styles.betCardTitle, { opacity: 1 }]}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: isCompactLayout ? 8 : 12 }}>
                                    {/* ⭐ PLAYER HEADSHOT - Larger circular avatar */}
                                    {/* For tennis/individual sports: use home_logo or away_logo as player photo */}
                                    {(() => {
                                        const sport = workingOption?.raw?.sport || option?.raw?.sport || '';
                                        const league = workingOption?.raw?.league || option?.raw?.league || '';
                                        const title = option?.title || workingOption?.raw?.market || '';
                                        const description = option?.description || workingOption?.raw?.selection || '';
                                        const { isSinglePlayer, isCombatSport } = detectSportType(sport, league, title, description);
                                        
                                        // Only show single headshot for tennis/golf (single player sports)
                                        // Combat sports (MMA/boxing) should show BOTH fighters in team logos area instead
                                        if (isCombatSport) return null;
                                        
                                        // Priority: explicit headshot > home_logo for single player sports > away_logo
                                        // ⭐ PLAYER PROPS: Always try to show player headshot for player props
                                        const isPlayerProp = (workingOption?.raw?.market || '').toLowerCase().includes('player') ||
                                                            (option?.title || '').toLowerCase().includes('player');
                                        const headshotUrl = 
                                            workingOption?._player_headshot ||
                                            workingOption?._headshot_url ||
                                            workingOption?.raw?._player_headshot || 
                                            workingOption?.raw?._headshot_url ||
                                            option?._player_headshot ||
                                            option?._headshot_url ||
                                            // For tennis/golf (single player), use home or away logo as player photo
                                            (isSinglePlayer ? (homeLogoUrl || awayLogoUrl || workingOption?.raw?.home_logo || workingOption?.raw?.away_logo) : null);
                                        
                                        // Show headshot for player props OR single player sports
                                        if (!headshotUrl && !isPlayerProp) return null;
                                        if (!headshotUrl) return null;
                                        
                                        return (
                                            <View style={{
                                                width: isCompactLayout ? 36 : 44,
                                                height: isCompactLayout ? 36 : 44,
                                                borderRadius: isCompactLayout ? 18 : 22,
                                                borderWidth: 2,
                                                borderColor: 'rgba(255, 255, 255, 0.25)',
                                                backgroundColor: 'rgba(30, 30, 30, 0.8)',
                                                overflow: 'hidden',
                                            }}>
                                                <Image 
                                                    source={{ uri: headshotUrl }}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        ...(Platform.OS === 'web' ? {
                                                            objectFit: 'cover',
                                                            objectPosition: 'center top',
                                                        } : {}),
                                                    }}
                                                    resizeMode="cover"
                                                />
                                            </View>
                                        );
                                    })()}
                                    {/* Title + Badges Container */}
                                    <View style={{ flex: 1, justifyContent: 'center' }}>
                                        {/* Row 1: Market Title + Position + Sportsbook badges */}
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: isCompactLayout ? 6 : 8, flexWrap: 'wrap' }}>
                                            <Text style={[styles.betCardTitleTextLarge, { opacity: isNoOddsYet ? 0.6 : 1, fontSize: isCompactLayout ? 14 : 17 }]}>{option.title}</Text>
                                            {/* Position Badge */}
                                            {(workingOption?.raw?._player_position || workingOption?._position || workingOption?.raw?._position) && (
                                                <View style={{
                                                    backgroundColor: 'rgba(59, 130, 246, 0.25)',
                                                    paddingHorizontal: 8,
                                                    paddingVertical: 3,
                                                    borderRadius: 6,
                                                    borderWidth: 1,
                                                    borderColor: 'rgba(59, 130, 246, 0.5)',
                                                }}>
                                                    <Text style={{ color: '#60a5fa', fontSize: 11, fontWeight: '700', letterSpacing: 0.5 }}>
                                                        {workingOption?.raw?._player_position || workingOption?._position || workingOption?.raw?._position}
                                                    </Text>
                                                </View>
                                            )}
                                    {(() => {
                                        // Determine which book to display
                                        let displayBook = selectedBook;
                                        
                                        // If "best" is selected, find which book has the best odds
                                        if (selectedBook === 'best' && workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) {
                                            let bestBook = null;
                                            let bestOdds = null;
                                            
                                            Object.entries(workingOption.bookOdds).forEach(([bookName, bookOddsValue]) => {
                                                const odds = extractPrice(bookOddsValue);
                                                if (odds !== null && odds !== undefined && !isNaN(odds)) {
                                                    if (bestOdds === null) {
                                                        bestBook = bookName;
                                                        bestOdds = odds;
                                                    } else {
                                                        // Best odds = highest positive OR least negative (closest to 0)
                                                        if (odds > 0 && bestOdds > 0) {
                                                            if (odds > bestOdds) {
                                                                bestBook = bookName;
                                                                bestOdds = odds;
                                                            }
                                                        } else if (odds < 0 && bestOdds < 0) {
                                                            if (odds > bestOdds) {
                                                                bestBook = bookName;
                                                                bestOdds = odds;
                                                            }
                                                        } else if (odds > 0) {
                                                            bestBook = bookName;
                                                            bestOdds = odds;
                                                        }
                                                    }
                                                }
                                            });
                                            
                                            displayBook = bestBook;
                                        }
                                        
                                        // Show the purple badge with the book name and logo
                                        if (displayBook && displayBook !== 'best') {
                                            const bookLogo = getSportsbookLogo(displayBook);
                                            return (
                                                <View style={{
                                                    backgroundColor: 'rgba(162, 89, 255, 0.25)',
                                                    paddingHorizontal: 10,
                                                    paddingVertical: 4,
                                                    borderRadius: 10,
                                                    borderWidth: 1,
                                                    borderColor: 'rgba(162, 89, 255, 0.6)',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                }}>
                                                    {bookLogo && (
                                                        <Image 
                                                            source={{ uri: bookLogo }} 
                                                            style={{ width: 14, height: 14, borderRadius: 3 }} 
                                                            resizeMode="contain" 
                                                        />
                                                    )}
                                                    <Text style={{ color: 'rgba(162, 89, 255, 0.95)', fontSize: 11, fontWeight: '700' }}>
                                                        {displayBook}
                                                    </Text>
                                                </View>
                                            );
                                        }
                                        
                                        return null;
                                    })()}
                                    {/* ⭐ MAIN vs ALT LINE BADGE - Only for PLAYER PROPS */}
                                    {(() => {
                                        // Only show MAIN/ALT badges for player props
                                        const market = (workingOption?.raw?.market || workingOption?.title || '').toLowerCase();
                                        const isPlayerProp = market.includes('player') || 
                                                            market.includes('points') ||
                                                            market.includes('rebounds') ||
                                                            market.includes('assists') ||
                                                            market.includes('threes') ||
                                                            market.includes('passing') ||
                                                            market.includes('rushing') ||
                                                            market.includes('receiving') ||
                                                            market.includes('touchdown');
                                        
                                        if (!isPlayerProp) return null;
                                        
                                        const isMain = workingOption?.is_main === true || workingOption?.raw?.is_main === true;
                                        const isAlt = workingOption?.is_main === false || workingOption?.raw?.is_main === false;
                                        
                                        if (isMain) {
                                            return (
                                                <View style={{
                                                    backgroundColor: '#2E7D32',
                                                    paddingHorizontal: 8,
                                                    paddingVertical: 3,
                                                    borderRadius: 4,
                                                    marginLeft: 8,
                                                }}>
                                                    <Text style={{ color: '#FFFFFF', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 }}>
                                                        MAIN
                                                    </Text>
                                                </View>
                                            );
                                        }
                                        
                                        if (isAlt) {
                                            return (
                                                <View style={{
                                                    backgroundColor: '#E65100',
                                                    paddingHorizontal: 8,
                                                    paddingVertical: 3,
                                                    borderRadius: 4,
                                                    marginLeft: 8,
                                                }}>
                                                    <Text style={{ color: '#FFFFFF', fontSize: 10, fontWeight: '800', letterSpacing: 0.5 }}>
                                                        ALT
                                                    </Text>
                                                </View>
                                            );
                                        }
                                        
                                        return null;
                                    })()}
                                    {/* 🏥 INJURY BADGE */}
                                    {(() => {
                                        const injStatus = workingOption?.raw?._injury_status || workingOption?._injury_status || option?.raw?._injury_status || null;
                                        const injFlag = workingOption?.raw?._is_injured || workingOption?._is_injured || option?.raw?._is_injured || false;
                                        if (!injFlag || !injStatus) return null;
                                        
                                        const isOut = injStatus === 'out';
                                        const isDoubtful = injStatus === 'doubtful';
                                        const bgColor = isOut ? 'rgba(239, 68, 68, 0.2)' : isDoubtful ? 'rgba(239, 68, 68, 0.15)' : 'rgba(251, 191, 36, 0.2)';
                                        const borderColor = isOut ? 'rgba(239, 68, 68, 0.6)' : isDoubtful ? 'rgba(239, 68, 68, 0.4)' : 'rgba(251, 191, 36, 0.5)';
                                        
                                        return (
                                            <View style={{
                                                backgroundColor: bgColor,
                                                paddingHorizontal: 8,
                                                paddingVertical: 3,
                                                borderRadius: 4,
                                                borderWidth: 1,
                                                borderColor: borderColor,
                                                marginLeft: 8,
                                            }}>
                                                <Text style={{ color: '#fff', fontSize: 9, fontWeight: '700', letterSpacing: 0.5 }}>
                                                    🏥 {injStatus.toUpperCase()}
                                                </Text>
                                            </View>
                                        );
                                    })()}
                                        </View>
                                        {/* Row 2: Description (player name + line) */}
                                        <Text style={[styles.betCardDescriptionLarge, { opacity: isNoOddsYet ? 0.6 : 1, marginTop: 4, fontSize: isCompactLayout ? 12 : 14 }]}>
                                            {option.description}
                                        </Text>
                                        {/* 🏥 Injury detail line */}
                                        {(() => {
                                            const injStatus = workingOption?.raw?._injury_status || workingOption?._injury_status || null;
                                            const injFlag = workingOption?.raw?._is_injured || workingOption?._is_injured || false;
                                            const injDetails = workingOption?.raw?._injury_details || workingOption?._injury_details || null;
                                            if (!injFlag || !injDetails) return null;
                                            const color = injStatus === 'out' ? '#ef4444' : '#fbbf24';
                                            return (
                                                <Text style={{ fontSize: 11, color, marginTop: 2, opacity: 0.9 }} numberOfLines={1}>
                                                    🏥 {injDetails.injury_type || ''}{injDetails.body_part ? ` (${injDetails.body_part})` : ''}{injDetails.designation ? ` — ${injDetails.designation}` : ''}
                                                </Text>
                                            );
                                        })()}
                                    </View>
                                </View>
                            </View>
                            
                            {/* ⭐ Date and Time Display - ALWAYS SHOW with status like ticker */}
                          {(() => {
                            // ⭐ Parse date - handle Firestore timestamps
                            const parseDate = (dateField) => {
                              if (!dateField) return null;
                              try {
                                // Firestore timestamp { _seconds, _nanoseconds }
                                if (dateField._seconds) return new Date(dateField._seconds * 1000);
                                // Firestore timestamp { seconds, nanoseconds }
                                if (dateField.seconds) return new Date(dateField.seconds * 1000);
                                // Regular date
                                const d = new Date(dateField);
                                return isNaN(d.getTime()) ? null : d;
                              } catch { return null; }
                            };
                            
                            const d = parseDate(option?.date) || 
                                      parseDate(option?.raw?.start_date) || 
                                      parseDate(option?.raw?.commence_time) || 
                                      parseDate(option?.raw?.event_date) || 
                                      parseDate(option?.raw?.game_time) ||
                                      parseDate(option?.raw?.start_time);
                            
                            const now = new Date();
                            
                            // ⭐ Compute status from date like ticker does
                            let status = 'unknown';
                            let statusLabel = '';
                            let statusColor = '#6B7280';
                            let statusBgColor = 'rgba(107, 114, 128, 0.2)';
                            
                            if (d) {
                              const diffMs = d - now;
                              const diffHours = diffMs / (1000 * 60 * 60);
                              
                              if (diffHours < -4) {
                                status = 'ended';
                                statusLabel = 'FINAL';
                                statusColor = '#6B7280';
                                statusBgColor = 'rgba(107, 114, 128, 0.2)';
                              } else if (diffHours < 0) {
                                status = 'live';
                                statusLabel = 'LIVE';
                                statusColor = '#EF4444';
                                statusBgColor = 'rgba(239, 68, 68, 0.2)';
                              } else if (diffHours < 1) {
                                status = 'soon';
                                statusLabel = 'SOON';
                                statusColor = '#F59E0B';
                                statusBgColor = 'rgba(245, 158, 11, 0.2)';
                              } else {
                                status = 'upcoming';
                                statusLabel = 'UPCOMING';
                                statusColor = '#3B82F6';
                                statusBgColor = 'rgba(59, 130, 246, 0.2)';
                              }
                            }
                            
                            // Format date/time
                            let dateLabel = 'TBD';
                            let timeStr = '';
                            
                            if (d) {
                              const isToday = d.toDateString() === now.toDateString();
                              const tomorrow = new Date(now);
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              const isTomorrow = d.toDateString() === tomorrow.toDateString();
                              
                              if (isToday) dateLabel = 'Today';
                              else if (isTomorrow) dateLabel = 'Tomorrow';
                              else dateLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                              
                              timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                            }
                            
                            return (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                                {/* ⭐ Status Badge - ALWAYS show */}
                                {statusLabel && (
                                  <View style={{
                                    backgroundColor: statusBgColor,
                                    paddingHorizontal: 8,
                                    paddingVertical: 3,
                                    borderRadius: 6,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}>
                                    {status === 'live' && (
                                      <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#EF4444' }} />
                                    )}
                                    <Text style={{ color: statusColor, fontSize: 10, fontWeight: '700', letterSpacing: 0.5 }}>
                                      {statusLabel}
                                    </Text>
                                  </View>
                                )}
                                
                                {/* Date and Time */}
                                <Text style={{ color: 'rgba(162, 89, 255, 0.95)', fontSize: 12, fontWeight: '600' }}>{dateLabel}</Text>
                                {timeStr && status !== 'ended' && (
                                  <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12, fontWeight: '500' }}>{timeStr}</Text>
                                )}
                              </View>
                            );
                          })()}
                          
                          {/* Team Logos - Show for team sports and combat sports */}
                            {(() => {
                              const sport = workingOption?.raw?.sport || option?.raw?.sport || '';
                              const league = workingOption?.raw?.league || option?.raw?.league || '';
                              const title = option?.title || workingOption?.raw?.market || '';
                              const description = option?.description || workingOption?.raw?.selection || '';
                              const { isSinglePlayer, isCombatSport } = detectSportType(sport, league, title, description);
                              
                              // Don't show team logos for single-player sports (tennis/golf) - headshot is shown in header
                              if (isSinglePlayer) return null;
                              
                              // Need at least one logo to show
                              if (!(homeLogoUrl || awayLogoUrl || teamLogoUrl)) return null;
                              
                              // ⭐ For combat sports: Dedupe if both URLs are the same
                              const showAway = awayLogoUrl;
                              const showHome = homeLogoUrl && homeLogoUrl !== awayLogoUrl;
                              
                              return (
                                <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                                  {showAway ? (<Image source={{ uri: awayLogoUrl }} style={{ width: isCompactLayout ? 28 : 36, height: isCompactLayout ? 28 : 36, borderRadius: 6 }} resizeMode="contain" />) : null}
                                  {showHome ? (<Image source={{ uri: homeLogoUrl }} style={{ width: isCompactLayout ? 28 : 36, height: isCompactLayout ? 28 : 36, borderRadius: 6 }} resizeMode="contain" />) : null}
                                  {teamLogoUrl && !awayLogoUrl && !homeLogoUrl ? (<Image source={{ uri: teamLogoUrl }} style={{ width: isCompactLayout ? 28 : 36, height: isCompactLayout ? 28 : 36, borderRadius: 6 }} resizeMode="contain" />) : null}
                                </View>
                              );
                            })()}
                              </View>
                        
                        {/* ROW 2: Math Section (inside left column) */}
                    {canShowMath && showMath && (
                            <View style={[styles.mathSectionInline, { opacity: 1 }]}>
                                {/* EV% Devigging Methods */}
                                {/* ⭐ RESPONSIVE: Wrap on mobile */}
                                {displayEvMethods && (
                                <View style={[styles.metricsRow, { marginBottom: 12, opacity: 1 }, isCompactLayout && { flexWrap: 'wrap' }]}>
                                    <View style={[styles.metricCol, styles.metricColFirst, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', marginBottom: 8 }]}>
                                        <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Additive EV%</Text>
                                        <Text style={[
                                            styles.metricValue, 
                                                (displayEvMethods.additive?.ev || 0) > 0 ? styles.evPositive : styles.evNegative,
                                            { opacity: 1, fontSize: isCompactLayout ? 14 : 18 }
                                        ]}>
                                                {displayEvMethods.additive?.ev != null ? displayEvMethods.additive.ev.toFixed(2) : (ev != null ? ev.toFixed(2) : 'N/A')}%
                                        </Text>
                                            {displayEvMethods.methodUsed === 'additive' && (
                                            <Text style={[styles.metricSubtext, { color: '#4ADE80', opacity: 1 }]}>✓ Shown</Text>
                                        )}
                                    </View>
                                    <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', marginBottom: 8, borderLeftWidth: 0 }]}>
                                        <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Mult. EV%</Text>
                                        <Text style={[
                                            styles.metricValue, 
                                                (displayEvMethods.multiplicative?.ev || 0) > 0 ? styles.evPositive : styles.evNegative,
                                            { opacity: 1, fontSize: isCompactLayout ? 14 : 18 }
                                        ]}>
                                                {displayEvMethods.multiplicative?.ev != null ? displayEvMethods.multiplicative.ev.toFixed(2) : (ev != null ? ev.toFixed(2) : 'N/A')}%
                                        </Text>
                                            {displayEvMethods.methodUsed === 'multiplicative' && (
                                            <Text style={[styles.metricSubtext, { color: '#4ADE80', opacity: 1 }]}>✓ Shown</Text>
                                        )}
                                    </View>
                                    <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '100%', borderLeftWidth: 0 }]}>
                                        <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Power EV%</Text>
                                        <Text style={[
                                            styles.metricValue, 
                                                displayEvMethods.power ? ((displayEvMethods.power.ev || 0) > 0 ? styles.evPositive : styles.evNegative) : { opacity: 1 },
                                            { opacity: 1, fontSize: isCompactLayout ? 14 : 18 }
                                        ]}>
                                                {displayEvMethods.power?.ev != null ? displayEvMethods.power.ev.toFixed(2) : (ev != null ? ev.toFixed(2) : 'N/A')}%
                                        </Text>
                                            {displayEvMethods.methodUsed === 'power' && (
                                            <Text style={[styles.metricSubtext, { color: '#4ADE80', opacity: 1 }]}>✓ Shown</Text>
                                        )}
                                    </View>
                                </View>
                            )}
                            
                                {/* Price and Fair Value Row */}
                            {/* ⭐ RESPONSIVE: 2x2 grid on mobile, 1x4 on desktop */}
                            <View style={[styles.metricsRow, { opacity: 1 }, isCompactLayout && { flexWrap: 'wrap' }]}>
                                <View style={[styles.metricCol, styles.metricColFirst, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', marginBottom: 8 }]}>
                                    {/* ⭐ ENHANCED: Show sportsbook logo + name when specific book selected */}
                                    {selectedBook !== 'best' ? (
                                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                        {getSportsbookLogo(selectedBook) ? (
                                          <Image 
                                            source={{ uri: getSportsbookLogo(selectedBook) }} 
                                            style={{ width: 16, height: 16, borderRadius: 3 }} 
                                            resizeMode="contain" 
                                          />
                                        ) : null}
                                        <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11, color: '#A259FF' }]}>
                                          {selectedBook}
                                        </Text>
                                      </View>
                                    ) : (
                                      <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>
                                        Best Odds
                                      </Text>
                                    )}
                                    <Text style={[styles.metricValue, { opacity: 1, fontSize: isCompactLayout ? 15 : 18 }]}>{price !== null ? `${price > 0 ? '+' : ''}${price}` : '—'}</Text>
                                    <Text style={[styles.metricSubtext, { opacity: 1 }]}>{implied !== null ? `${(implied * 100).toFixed(1)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', marginBottom: 8, borderLeftWidth: 0 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Market Average</Text>
                                    <Text style={[styles.metricValue, { opacity: 1, fontSize: isCompactLayout ? 15 : 18 }]}>{marketAverage !== null ? `${marketAverage > 0 ? '+' : ''}${marketAverage}` : '—'}</Text>
                                    <Text style={[styles.metricSubtext, { opacity: 1 }]}>{marketAverageImplied !== null ? `${(marketAverageImplied * 100).toFixed(1)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', borderLeftWidth: 0 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>EV%</Text>
                                        <Text style={[styles.metricValue, ev !== null ? (ev >= 0 ? styles.evPositive : styles.evNegative) : null, { opacity: 1, fontSize: isCompactLayout ? 15 : 18 }]}>{ev !== null ? `${ev >= 0 ? '+' : ''}${ev.toFixed(2)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', borderLeftWidth: 0 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Books</Text>
                                    <Text style={[styles.metricValue, { opacity: 1, fontSize: isCompactLayout ? 15 : 18 }]}>{bookCount}</Text>
                                </View>
                            </View>

                                {/* Kelly Criterion Row */}
                            {/* ⭐ RESPONSIVE: 2x2 grid on mobile, 1x4 on desktop */}
                            <View style={[styles.metricsRow, { marginTop: 12, opacity: 1 }, isCompactLayout && { flexWrap: 'wrap' }]}>
                                <View style={[styles.metricCol, styles.metricColFirst, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', marginBottom: 8 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Kelly Units</Text>
                                    <Text style={[styles.metricValue, { opacity: 1, fontSize: isCompactLayout ? 15 : 18 }]}>{kellyUnits !== null ? `${(kellyUnits * 100).toFixed(1)}%` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', marginBottom: 8, borderLeftWidth: 0 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Suggested Wager</Text>
                                    <Text style={[styles.metricValue, { opacity: 1, fontSize: isCompactLayout ? 15 : 18 }]}>{suggestedWager !== null ? `$${suggestedWager}` : '—'}</Text>
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', borderLeftWidth: 0 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Your Stake</Text>
                                    <TextInput
                                        style={[styles.stakeInputInline, isCompactLayout && { fontSize: 14 }]}
                                        value={String(stakeNum)}
                                        onChangeText={(t) => {
                                            const n = parseFloat(String(t).replace(/[^0-9.]/g, ''));
                                            if (!isNaN(n)) setStake(n); else if (t === '') setStake(0);
                                        }}
                                        keyboardType="numeric"
                                        placeholder="10"
                                        placeholderTextColor="rgba(255,255,255,0.5)"
                                    />
                                </View>
                                <View style={[styles.metricCol, styles.metricColSep, { opacity: 1 }, isCompactLayout && { flex: 0, width: '48%', borderLeftWidth: 0 }]}>
                                    <Text style={[styles.metricLabel, { opacity: 1, fontSize: isCompactLayout ? 10 : 11 }]}>Payout</Text>
                                    <Text style={[styles.metricValue, { opacity: 1, fontSize: isCompactLayout ? 15 : 18 }]}>{payout !== null ? `$${payout.toFixed(2)}` : '—'}</Text>
                                </View>
                            </View>

                            {/* Quick stake buttons */}
                            {/* ⭐ RESPONSIVE: Smaller buttons on mobile */}
                            <View style={[styles.stakeQuickRow, { marginTop: 8, opacity: 1, gap: isCompactLayout ? 6 : 8 }]}>
                                {[5, 10, 25, 50].map((amt) => (
                                    <TouchableOpacity key={amt} onPress={() => setStake(amt)} style={[styles.stakeChip, stakeNum === amt ? styles.stakeChipActive : null, { opacity: 1 }]}>
                                        <Text style={[styles.stakeChipText, stakeNum === amt ? styles.stakeChipTextActive : null, { opacity: 1 }]}>${amt}</Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                                        </View>
                        )}
                        
                        {/* ROW 3: Action Buttons (inside left column) */}
                        {/* ⭐ RESPONSIVE: Smaller gap on mobile, buttons wrap */}
                        {!isNoOddsYet && (
                            <View style={[styles.betCardActionsInline, isCompactLayout && { gap: 8 }]} onStartShouldSetResponder={() => true} onTouchEnd={(e) => e.stopPropagation()}>
                                <View style={isCompactLayout ? { minWidth: '45%' } : {}}>
                                    <GradientButton
                                        text={isSelected ? "Deselect Leg" : "Select Leg"}
                                        onPress={(e) => { e?.stopPropagation?.(); handleSelectLeg(); }}
                                        style={[styles.betCardButton, isCompactLayout && { paddingHorizontal: 10 }]}
                                        textStyle={isCompactLayout ? { fontSize: 12 } : undefined}
                                    />
                                </View>
                                {/* Deep Link Button - uses selected book's deep link or fallback to primary */}
                                {(() => {
                                    // ⭐ Helper: Extract URL from deep_link (handles both string and object format)
                                    const extractDeepLink = (dl) => {
                                        if (!dl) return null;
                                        if (typeof dl === 'string') return dl;
                                        // Object format: { android, desktop, ios }
                                        if (typeof dl === 'object') {
                                            // For web, prefer desktop URL
                                            return dl.desktop || dl.ios || dl.android || null;
                                        }
                                        return null;
                                    };
                                    
                                    // Try to get deep link from selected sportsbook first
                                    let deepLink = null;
                                    let displayBookName = selectedBook;
                                    let isDirectLink = false; // ⭐ Track if it's a real deep link vs fallback
                                    
                                    if (selectedBook !== 'best' && workingOption?.bookOdds?.[selectedBook]) {
                                        const bookData = workingOption.bookOdds[selectedBook];
                                        if (typeof bookData === 'object') {
                                            deepLink = extractDeepLink(bookData.deep_link) || extractDeepLink(bookData.deeplink) || bookData.url;
                                            if (deepLink) isDirectLink = true;
                                        }
                                    }
                                    
                                    // If no book-specific link, find best book's link or use primary
                                    if (!deepLink && selectedBook === 'best' && workingOption?.bookOdds) {
                                        // Find best book and its deep link
                                        let bestBook = null;
                                        let bestOdds = null;
                                        Object.entries(workingOption.bookOdds).forEach(([bookName, bookData]) => {
                                            const odds = extractPrice(bookData);
                                            if (odds !== null && (bestOdds === null || odds > bestOdds)) {
                                                bestOdds = odds;
                                                bestBook = bookName;
                                                if (typeof bookData === 'object') {
                                                    const foundLink = extractDeepLink(bookData.deep_link) || extractDeepLink(bookData.deeplink) || bookData.url;
                                                    if (foundLink) {
                                                        deepLink = foundLink;
                                                        isDirectLink = true;
                                                    }
                                                }
                                            }
                                        });
                                        displayBookName = bestBook || 'Sportsbook';
                                    }
                                    
                                    // Fallback to primary URLs
                                    if (!deepLink) {
                                        const urls = option?.raw?.urls || {};
                                        deepLink = urls.offer || urls.deeplink || urls.event || urls.market || null;
                                        if (deepLink) isDirectLink = true;
                                    }
                                    
                                    // ⭐ FALLBACK: Use sportsbook homepage URL if no deep link available
                                    if (!deepLink) {
                                        const fallbackUrl = getSportsbookUrl(displayBookName);
                                        if (fallbackUrl) {
                                            deepLink = fallbackUrl;
                                            isDirectLink = false; // This is NOT a direct link to bet
                                            console.log('[DeepLink] Using sportsbook homepage fallback for:', displayBookName, fallbackUrl);
                                        }
                                    }
                                    
                                    // ⭐ SECOND FALLBACK: Try ANY available book in bookOdds
                                    if (!deepLink && workingOption?.bookOdds) {
                                        const availableBooks = Object.keys(workingOption.bookOdds);
                                        for (const bookName of availableBooks) {
                                            const fallbackUrl = getSportsbookUrl(bookName);
                                            if (fallbackUrl) {
                                                deepLink = fallbackUrl;
                                                displayBookName = bookName;
                                                isDirectLink = false;
                                                console.log('[DeepLink] Using first available book as fallback:', bookName, fallbackUrl);
                                                break;
                                            }
                                        }
                                    }
                                    
                                    // ⭐ FINAL FALLBACK: Generic sportsbook search
                                    if (!deepLink) {
                                        const teamContext = workingOption?.homeTeam || workingOption?.raw?.home_team || option?.raw?.fixture?.home_team || '';
                                        if (teamContext) {
                                            deepLink = `https://www.google.com/search?q=${encodeURIComponent(teamContext + ' betting odds sportsbook')}`;
                                            displayBookName = 'Sportsbooks';
                                            isDirectLink = false;
                                            console.log('[DeepLink] Using Google search fallback for:', teamContext);
                                        }
                                    }
                                    
                                    // Debug: Log what we found
                                    if (!deepLink && workingOption?.bookOdds) {
                                        const firstBook = Object.entries(workingOption.bookOdds)[0];
                                        if (firstBook) {
                                            console.log('[DeepLink] No deepLink found. First book data:', firstBook[0], firstBook[1]?.deep_link);
                                        }
                                    }
                                    
                                    if (!deepLink) return null;
                                    
                                    const bookDisplayText = displayBookName !== 'best' ? displayBookName : 'Sportsbook';
                                    // ⭐ Different text for direct links vs fallback homepage links
                                    const buttonText = isDirectLink 
                                        ? `Place Bet on ${bookDisplayText}` 
                                        : `Visit ${bookDisplayText}`;
                                    
                                    console.log('[DeepLink] 🔗 Rendering button for:', displayBookName, '| isDirectLink:', isDirectLink, '| URL:', typeof deepLink === 'object' ? JSON.stringify(deepLink) : deepLink);
                                    
                                    return (
                                      <View>
                                        <GradientButton
                                          text={buttonText}
                                          onPress={(e) => {
                                            console.log('[DeepLink] 🖱️ Button PRESSED!', {
                                              deepLink,
                                              selectedBook,
                                              displayBookName,
                                              isDirectLink,
                                              hasOnOpenDeepLink: !!onOpenDeepLink
                                            });
                                            e?.stopPropagation?.();
                                            if (onOpenDeepLink) { 
                                              console.log('[DeepLink] 📤 Calling onOpenDeepLink handler...');
                                              onOpenDeepLink({ ...option, _selectedBook: selectedBook, _deepLink: deepLink, _isDirectLink: isDirectLink }); 
                                              return; 
                                            }
                                            // Open deep link URL in new tab (fallback if no handler)
                                            console.log('[DeepLink] 🌐 Opening directly (no handler)...');
                                            let finalUrl = deepLink;
                                            if (typeof deepLink === 'object') {
                                              finalUrl = deepLink.desktop || deepLink.ios || deepLink.android;
                                            }
                                            if (typeof finalUrl === 'string' && finalUrl.startsWith('http')) {
                                              window.open(finalUrl, '_blank', 'noopener,noreferrer');
                                            } else {
                                              console.warn('[DeepLink] ⚠️ Invalid URL:', finalUrl);
                                            }
                                          }}
                                          style={[
                                            styles.betCardButton,
                                            // ⭐ Subtle visual differentiation for fallback links - dotted border
                                            !isDirectLink && { 
                                              borderStyle: 'dashed',
                                              borderColor: 'rgba(255, 255, 255, 0.25)',
                                            }
                                          ]}
                                          // ⭐ Muted colors for fallback links vs bright for direct links
                                          gradientColors={isDirectLink 
                                            ? ["#10B981", "#34D399"]  // Green gradient for direct bet links
                                            : ["#6B7280", "#9CA3AF"]  // Gray gradient for homepage fallbacks
                                          }
                                        />
                                      </View>
                                    );
                                })()}
                                {canShowMath ? (
                                  <View>
                                      <GradientButton
                                          text={showMath ? "Hide Math" : "Show Math"}
                                          onPress={(e) => { e?.stopPropagation?.(); handleToggleMath(); }}
                                          style={styles.betCardButton}
                                      />
                                  </View>
                                ) : null}
                        </View>
                    )}
                    
                        {/* Watch button for "no odds yet" cards */}
                    {isNoOddsYet && !isLive && !isEnded && (
                            <View style={{ paddingVertical: 12, alignItems: 'flex-start' }}>
                            {isWatching ? (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                        <View style={{
                                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                            paddingHorizontal: 12,
                                            paddingVertical: 6,
                                            borderRadius: 16,
                                            borderWidth: 1,
                                            borderColor: 'rgba(76, 175, 80, 0.5)',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 6,
                                        }}>
                                            <Text style={{ fontSize: 16 }}>👁️</Text>
                                            <Text style={{ color: '#4CAF50', fontSize: 12, fontWeight: '700' }}>Watching</Text>
                                        </View>
                                        <TouchableOpacity
                                            onPress={(e) => {
                                                e?.stopPropagation?.();
                                                if (onUnwatchFixture) {
                                                    onUnwatchFixture(option);
                                                }
                                            }}
                                            style={{
                                                backgroundColor: 'rgba(244, 67, 54, 0.25)',
                                                paddingHorizontal: 12,
                                                paddingVertical: 6,
                                                borderRadius: 16,
                                                borderWidth: 1,
                                                borderColor: 'rgba(244, 67, 54, 0.6)',
                                            }}
                                        >
                                            <Text style={{ color: '#FF5252', fontSize: 12, fontWeight: '700' }}>
                                                Stop Watching
                                            </Text>
                                        </TouchableOpacity>
                                    </View>
                            ) : (
                                    <GradientButton
                                        text="🔔 Watch & Notify Me"
                                        onPress={(e) => { 
                                            e?.stopPropagation?.(); 
                                            if (onWatchFixture) {
                                                onWatchFixture(option);
                                            }
                                        }}
                                        style={{ minWidth: 200 }}
                                        gradientColors={["rgba(255, 193, 7, 0.3)", "rgba(255, 193, 7, 0.2)"]}
                                    />
                            )}
                        </View>
                    )}
                    </View>
                    
                    {/* RIGHT COLUMN: All Sportsbooks (scrollable when many books) */}
                    {/* ⭐ RESPONSIVE: Full width on mobile, side column on desktop */}
                    <View style={[
                        styles.rightColumnFullHeight,
                        isMobileLayout && { 
                            width: '100%', 
                            maxWidth: '100%', 
                            borderLeftWidth: 0, 
                            borderTopWidth: 1, 
                            borderTopColor: 'rgba(255,255,255,0.08)',
                            paddingLeft: 0,
                            paddingTop: 12,
                            maxHeight: isCompactLayout ? 200 : 280, // Shorter on phone
                        }
                    ]}>
                        {/* Sportsbook list - show if we have bookOdds OR availableBooks (don't require logos) */}
                        {(workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0) || (workingOption?.availableBooks?.length > 0) ? (
                          (() => {
                            const bookCount = workingOption.availableBooks?.length || Object.keys(workingOption.bookOdds || {}).length;
                            // Each row is ~34px (6 padding * 2 + 22 logo height). Header ~30px. 
                            // On mobile: shorter max height. Show scroll indicator if many books.
                            const isScrollable = isMobileLayout ? bookCount > 4 : bookCount > 7;
                            
                            return (
                              <>
                                {/* Header row with book count and sort toggles */}
                                <View style={{ 
                                  flexDirection: 'row', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between', 
                                  marginBottom: 6,
                                  paddingBottom: 6,
                                  borderBottomWidth: 1,
                                  borderBottomColor: 'rgba(255,255,255,0.08)',
                                }}>
                                  <View style={{ flexDirection: 'column', gap: 2 }}>
                                    <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, fontWeight: '600' }}>
                                      {bookCount} {bookCount === 1 ? 'book' : 'books'}
                                    </Text>
                                    {/* ⭐ Hint that books are clickable */}
                                    <Text style={{ color: 'rgba(162, 89, 255, 0.6)', fontSize: 8, fontStyle: 'italic' }}>
                                      Click to select book
                                    </Text>
                                  </View>
                                  <View style={{ flexDirection: 'row', gap: 3 }}>
                                    <TouchableOpacity
                                      onPress={(e) => { e?.stopPropagation?.(); setSortBooksBy('ev'); }}
                                      style={{
                                        paddingVertical: 2, paddingHorizontal: 5, borderRadius: 4,
                                        backgroundColor: sortBooksBy === 'ev' ? 'rgba(74, 222, 128, 0.2)' : 'transparent',
                                        ...(Platform.OS === 'web' && { cursor: 'pointer' }),
                                      }}
                                    >
                                      <Text style={{ color: sortBooksBy === 'ev' ? '#4ade80' : 'rgba(255,255,255,0.4)', fontSize: 9, fontWeight: '600' }}>EV%</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                      onPress={(e) => { e?.stopPropagation?.(); setSortBooksBy('odds'); }}
                                      style={{
                                        paddingVertical: 2, paddingHorizontal: 5, borderRadius: 4,
                                        backgroundColor: sortBooksBy === 'odds' ? 'rgba(162, 89, 255, 0.2)' : 'transparent',
                                        ...(Platform.OS === 'web' && { cursor: 'pointer' }),
                                      }}
                                    >
                                      <Text style={{ color: sortBooksBy === 'odds' ? '#c4b5fd' : 'rgba(255,255,255,0.4)', fontSize: 9, fontWeight: '600' }}>Odds</Text>
                                    </TouchableOpacity>
                                    {/* My Books tab - shows user's preferred sportsbooks first */}
                                    {userSelectedSportsbooks?.length > 0 && (
                                      <TouchableOpacity
                                        onPress={(e) => { e?.stopPropagation?.(); setSortBooksBy('mine'); }}
                                        style={{
                                          paddingVertical: 2, paddingHorizontal: 5, borderRadius: 4,
                                          backgroundColor: sortBooksBy === 'mine' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                                          ...(Platform.OS === 'web' && { cursor: 'pointer' }),
                                        }}
                                      >
                                        <Text style={{ color: sortBooksBy === 'mine' ? '#60A5FA' : 'rgba(255,255,255,0.4)', fontSize: 9, fontWeight: '600' }}>Mine</Text>
                                      </TouchableOpacity>
                                    )}
                                  </View>
                                </View>
                                
                                {/* Scrollable book list */}
                                {/* ⭐ RESPONSIVE: Shorter max height on mobile */}
                                <ScrollView 
                                  style={{ maxHeight: isCompactLayout ? 160 : (isMobileLayout ? 200 : 280), flex: 0 }}
                                  contentContainerStyle={{ gap: 2, paddingBottom: 4 }}
                                  showsVerticalScrollIndicator={true}
                                  nestedScrollEnabled={true}
                                >
                                  {/* ⭐ PREFERRED SPORTSBOOK HEADER - Show when there's a pre-selected book */}
                                  {highlightPreferredBook && defaultSelectedBook && (
                                    <View style={{
                                      backgroundColor: 'rgba(74, 222, 128, 0.1)',
                                      borderRadius: 6,
                                      paddingVertical: 4,
                                      paddingHorizontal: 8,
                                      marginBottom: 4,
                                      borderWidth: 1,
                                      borderColor: 'rgba(74, 222, 128, 0.2)',
                                    }}>
                                      <Text style={{ 
                                        color: 'rgba(74, 222, 128, 0.9)', 
                                        fontSize: 9, 
                                        fontWeight: '700',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                      }}>
                                        🎯 Showing {defaultSelectedBook}
                                      </Text>
                                    </View>
                                  )}
                                  
                                  {/* "Best" row - only show when NOT highlighting a preferred book */}
                                  {!highlightPreferredBook && (
                                  <TouchableOpacity 
                                    onPress={(e) => { e?.stopPropagation?.(); setSelectedBook('best'); }}
                                    style={{ 
                                      flexDirection: 'row', alignItems: 'center', gap: 6,
                                      paddingVertical: 5, paddingHorizontal: 6, borderRadius: 6,
                                      backgroundColor: selectedBook === 'best' ? 'rgba(162, 89, 255, 0.2)' : 'transparent',
                                      borderWidth: selectedBook === 'best' ? 1 : 0,
                                      borderColor: 'rgba(162, 89, 255, 0.4)',
                                      ...(Platform.OS === 'web' && { cursor: 'pointer' }), // ⭐ Make clickable on web
                                    }}
                                  >
                                    <View style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: 'rgba(74, 222, 128, 0.2)', justifyContent: 'center', alignItems: 'center' }}>
                                      <Text style={{ fontSize: 10 }}>⭐</Text>
                                    </View>
                                    <Text style={{ color: '#4ade80', fontSize: 13, fontWeight: '700', minWidth: 42 }}>
                                      {sortBooksBy === 'ev' && bestEvData.odds !== null 
                                        ? (bestEvData.odds > 0 ? `+${bestEvData.odds}` : `${bestEvData.odds}`)
                                        : bestPrice !== null ? (bestPrice > 0 ? `+${bestPrice}` : `${bestPrice}`) : 'N/A'}
                                    </Text>
                                    {sortBooksBy === 'ev' && bestEvData.ev !== null && (
                                      <Text style={{ 
                                        color: bestEvData.ev >= 0 ? '#4ade80' : '#ef4444', fontSize: 10, fontWeight: '600',
                                        backgroundColor: bestEvData.ev >= 0 ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                        paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4,
                                      }}>
                                        {bestEvData.ev >= 0 ? '+' : ''}{bestEvData.ev.toFixed(1)}%
                                      </Text>
                                    )}
                                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, fontWeight: '500', flex: 1 }} numberOfLines={1}>Best</Text>
                                  </TouchableOpacity>
                                  )}
                                  
                                  {(() => {
                                    const booksList = workingOption.availableBooks?.length > 0 
                                      ? workingOption.availableBooks 
                                      : Object.keys(workingOption.bookOdds || {});
                                    
                                    // Helper to check if a book is in user's selected sportsbooks
                                    const isUserBook = (bookName) => {
                                      if (!userSelectedSportsbooks?.length) return true; // If no preferences, all are "user books"
                                      const bookLower = bookName.toLowerCase();
                                      return userSelectedSportsbooks.some(ub => 
                                        ub.toLowerCase() === bookLower || 
                                        bookLower.includes(ub.toLowerCase()) || 
                                        ub.toLowerCase().includes(bookLower)
                                      );
                                    };
                                    
                                    // ⭐ Helper to check if book matches preferred
                                    const isPreferred = (bookName) => {
                                      if (!highlightPreferredBook || !defaultSelectedBook) return false;
                                      const bookLower = bookName.toLowerCase().replace(/[^a-z0-9]/g, '');
                                      const prefLower = defaultSelectedBook.toLowerCase().replace(/[^a-z0-9]/g, '');
                                      return bookLower === prefLower || 
                                             bookLower.includes(prefLower) || 
                                             prefLower.includes(bookLower);
                                    };
                                    
                                    const sortedBooks = [...booksList].sort((a, b) => {
                                      const bookDataA = workingOption.bookOdds?.[a];
                                      const bookDataB = workingOption.bookOdds?.[b];
                                      const oddsA = extractPrice(bookDataA);
                                      const oddsB = extractPrice(bookDataB);
                                      
                                      // ⭐ FIRST: Preferred sportsbook always at top
                                      if (highlightPreferredBook && defaultSelectedBook) {
                                        const aIsPreferred = isPreferred(a);
                                        const bIsPreferred = isPreferred(b);
                                        if (aIsPreferred && !bIsPreferred) return -1;
                                        if (!aIsPreferred && bIsPreferred) return 1;
                                      }
                                      
                                      // "Mine" sort: user's books first, then by EV
                                      if (sortBooksBy === 'mine') {
                                        const aIsUser = isUserBook(a);
                                        const bIsUser = isUserBook(b);
                                        if (aIsUser && !bIsUser) return -1;
                                        if (!aIsUser && bIsUser) return 1;
                                        // Both same category - sort by EV
                                        const evA = typeof bookDataA === 'object' ? (bookDataA.ev_percent ?? null) : null;
                                        const evB = typeof bookDataB === 'object' ? (bookDataB.ev_percent ?? null) : null;
                                        if (evA === null && evB === null) return (oddsB ?? 0) - (oddsA ?? 0);
                                        if (evA === null) return 1;
                                        if (evB === null) return -1;
                                        return evB - evA;
                                      } else if (sortBooksBy === 'ev') {
                                        const evA = typeof bookDataA === 'object' ? (bookDataA.ev_percent ?? null) : null;
                                        const evB = typeof bookDataB === 'object' ? (bookDataB.ev_percent ?? null) : null;
                                        if (evA === null && evB === null) return (oddsB ?? 0) - (oddsA ?? 0);
                                        if (evA === null) return 1;
                                        if (evB === null) return -1;
                                        return evB - evA;
                                      } else {
                                        return (oddsB ?? 0) - (oddsA ?? 0);
                                      }
                                    });
                                    
                                    return sortedBooks.map((bookName) => {
                                      const logo = getSportsbookLogo(bookName);
                                      const bookData = workingOption.bookOdds?.[bookName];
                                      const odds = extractPrice(bookData);
                                      const formattedOdds = odds ? (odds > 0 ? `+${odds}` : `${odds}`) : 'N/A';
                                      const evPercent = typeof bookData === 'object' ? bookData.ev_percent : null;
                                      const hasEv = evPercent !== null && evPercent !== undefined;
                                      const isSelectedBook = selectedBook === bookName;
                                      // ⭐ Grey out sportsbooks not in user's preferences
                                      const isInUserBooks = isUserBook(bookName);
                                      const isGreyedByUserPrefs = userSelectedSportsbooks?.length > 0 && !isInUserBooks;
                                      
                                      // ⭐ Grey out non-preferred sportsbooks for "best X bets" queries
                                      // Check if this book matches the preferred book (case-insensitive)
                                      const bookNameLower = bookName.toLowerCase().replace(/[^a-z0-9]/g, '');
                                      const preferredLower = (defaultSelectedBook || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                                      const isPreferredBook = preferredLower && (
                                        bookNameLower === preferredLower ||
                                        bookNameLower.includes(preferredLower) ||
                                        preferredLower.includes(bookNameLower)
                                      );
                                      const isGreyedByPreferred = highlightPreferredBook && !isPreferredBook;
                                      
                                      // Apply grey-out if EITHER user prefs OR preferred book indicates it
                                      const isGreyedOut = isGreyedByUserPrefs || isGreyedByPreferred;
                                      
                                      // ⭐ Determine styling based on state
                                      const showMyBookIndicator = isInUserBooks && userSelectedSportsbooks?.length > 0;
                                      let bgColor = 'transparent';
                                      let borderW = 0;
                                      let borderC = 'transparent';
                                      
                                      if (isSelectedBook) {
                                        bgColor = 'rgba(162, 89, 255, 0.2)';
                                        borderW = 1;
                                        borderC = 'rgba(162, 89, 255, 0.4)';
                                      } else if (isPreferredBook && highlightPreferredBook) {
                                        bgColor = 'rgba(74, 222, 128, 0.15)';
                                        borderW = 1;
                                        borderC = 'rgba(74, 222, 128, 0.3)';
                                      } else if (showMyBookIndicator) {
                                        bgColor = 'rgba(139, 92, 246, 0.08)';
                                        borderW = 2;
                                        borderC = 'rgba(139, 92, 246, 0.5)';
                                      }
                                      
                                      return (
                                        <TouchableOpacity 
                                          key={bookName}
                                          onPress={(e) => { e?.stopPropagation?.(); setSelectedBook(bookName); }}
                                          activeOpacity={0.7}
                                          style={{ 
                                            flexDirection: 'row', alignItems: 'center', gap: 6,
                                            paddingVertical: 5, paddingHorizontal: 6, borderRadius: 6,
                                            backgroundColor: bgColor,
                                            borderWidth: borderW,
                                            borderColor: borderC,
                                            opacity: isGreyedOut ? 0.4 : 1,
                                            ...(Platform.OS === 'web' && { cursor: 'pointer' }), // ⭐ Make clickable on web
                                          }}
                                        >
                                          {/* ⭐ "My Books" star indicator */}
                                          {showMyBookIndicator && (
                                            <Text style={{ fontSize: 10, marginRight: -2, pointerEvents: 'none' }}>⭐</Text>
                                          )}
                                          {logo ? (
                                            <Image source={{ uri: logo }} style={{ width: 20, height: 20, borderRadius: 4, pointerEvents: 'none' }} resizeMode="contain" />
                                          ) : (
                                            <View style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.1)', justifyContent: 'center', alignItems: 'center', pointerEvents: 'none' }}>
                                              <Text style={{ fontSize: 8, color: 'rgba(255,255,255,0.5)' }}>📖</Text>
                                            </View>
                                          )}
                                          <Text style={{ color: '#4ade80', fontSize: 13, fontWeight: '700', minWidth: 42, pointerEvents: 'none' }}>{formattedOdds}</Text>
                                          {hasEv && (
                                            <Text style={{ 
                                              color: evPercent >= 0 ? '#4ade80' : '#ef4444', fontSize: 10, fontWeight: '600',
                                              backgroundColor: evPercent >= 0 ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                              paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4,
                                              pointerEvents: 'none',
                                            }}>
                                              {evPercent >= 0 ? '+' : ''}{evPercent.toFixed(1)}%
                                            </Text>
                                          )}
                                          <Text style={{ color: isSelectedBook ? 'rgba(255,255,255,0.9)' : (showMyBookIndicator ? '#c4b5fd' : 'rgba(255,255,255,0.6)'), fontSize: 10, fontWeight: '500', flex: 1, pointerEvents: 'none' }} numberOfLines={1}>
                                            {bookName}
                                          </Text>
                                        </TouchableOpacity>
                                      );
                                    });
                                  })()}
                                </ScrollView>
                                
                                {/* Scroll indicator at bottom when scrollable */}
                                {isScrollable && (
                                  <View style={{
                                    backgroundColor: 'rgba(162, 89, 255, 0.1)',
                                    paddingVertical: 4,
                                    paddingHorizontal: 8,
                                    borderRadius: 4,
                                    marginTop: 4,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 4,
                                  }}>
                                    <Text style={{ color: 'rgba(162, 89, 255, 0.8)', fontSize: 9, fontWeight: '600' }}>↕ Scroll for more</Text>
                                  </View>
                                )}
                              </>
                            );
                          })()
                        ) : (sportsbookLogoUrl || logoUrl) ? (
                          <Image source={{ uri: sportsbookLogoUrl || logoUrl }} style={{ width: 40, height: 40, borderRadius: 8, opacity: 0.95 }} resizeMode="contain" />
                        ) : !homeLogoUrl && !teamLogoUrl ? (
                          <Text style={[styles.betCardAmount, { opacity: 1 }]}>{option.amount}</Text>
                        ) : null}
                    </View>
                        </View>
                </View>
        </Pressable>
    );
}
